import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { imageService } from '@bedrock/fido-sdk';
import { DEFAULT_IMAGE_FORMAT, IMAGE_FIT_SCALE_CROP, IMAGE_QUALITY } from '@bedrock-rdk/constants';
import { cover } from '../../tornado/utils/textures';
import { getInitialStyle } from './jingleImages.style';

/**
 * A dumb, full-screen surface that renders a single jingle image (video / program /
 * before-ad / post-ad). It consolidates the image-loading boilerplate and the
 * fade-in/out + texture freeing; WHICH image to show and WHEN is decided by the
 * player (driven by the player mode).
 */
export class JingleImages extends Lightning.Component {
  static _template() {
    return _merge({ Image: {} }, getInitialStyle());
  }

  _attach() {
    this._hideAnimation = this.animation({
      duration: 0.5,
      actions: [{ p: 'alpha', v: { 0: 1, 1: 0 } }],
    });
    // Free the texture only once the surface is fully hidden to avoid a flash.
    this._hideAnimation.on('finish', () => this._freeTexture());
  }

  _detach() {
    if (this._hideAnimation) {
      this._hideAnimation.removeAllListeners('finish');
      this._hideAnimation = null;
    }
  }

  /**
   * Shows the given image. A falsy id hides the surface (e.g. jingle disabled).
   *
   * @param {string} imageId
   */
  show(imageId) {
    if (!imageId) {
      this.hide();

      return;
    }

    if (this._hideAnimation) {
      this._hideAnimation.stop();
    }

    const { finalW, finalH } = this;
    const view = this.tag('Image');
    view.texture = cover(
      imageService.getUrl(imageId, finalW, finalH, {
        fit: IMAGE_FIT_SCALE_CROP,
        quality: IMAGE_QUALITY,
        format: DEFAULT_IMAGE_FORMAT,
      }),
      finalW,
      finalH,
    );
    // NOTE: forcing to load a texture since the surface alpha is 0 at this moment.
    // If this call is omitted, there will be a flush of paused content before the image appears.
    view.loadTexture();
    this.setSmooth('alpha', 1);
  }

  hide() {
    if (this.alpha && this._hideAnimation) {
      this._hideAnimation.start();
    } else {
      this.alpha = 0;
      this._freeTexture();
    }
  }

  _freeTexture() {
    const view = this.tag('Image');

    try {
      if (view.texture) {
        view.texture.free();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[jingleImages] Could not free texture:', error);
    } finally {
      view.texture = null;
    }
  }
}
